import type { LobbyGame } from '../api/getLobby';
import type { GameTypeOption, LobbyFilters, SportOption } from '../components/LobbyFilterView';

function normalizedSportKey(sport: string | null | undefined): string {
  if (!sport) return '';
  return sport
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

/** Map API `sport` values to filter SportOption ids (best-effort). */
export function gameSportFilterOption(game: LobbyGame): SportOption | null {
  const n = normalizedSportKey(game.sport);
  if (!n) return null;

  const map: Record<string, SportOption> = {
    nfl: 'nfl',
    ncaafb: 'nfl',
    ncaaf: 'nfl',
    cfb: 'nfl',
    nba: 'nba',
    ncaab: 'nba',
    cbb: 'nba',
    wnba: 'nba',
    mlb: 'mlb',
    nhl: 'ice_hockey',
    ice_hockey: 'ice_hockey',
    pga: 'golf',
    mma: 'ufc',
    nascar: 'nascar',
    golf: 'golf',
    soccer: 'soccer',
    football: 'soccer',
    tennis: 'tennis',
    ufc: 'ufc',
    mls: 'mls',
    premier_league: 'premier_league',
    epl: 'premier_league',
    table_tennis: 'table_tennis',
    track_and_field: 'track_and_field',
    the_masters: 'the_masters',
    masters: 'the_masters',
  };

  if (map[n]) return map[n];

  if (n === 'mixed') return null;

  const allSports: SportOption[] = [
    'nba',
    'nfl',
    'mlb',
    'soccer',
    'tennis',
    'golf',
    'ice_hockey',
    'table_tennis',
    'track_and_field',
    'mls',
    'ufc',
    'nascar',
    'the_masters',
    'premier_league',
  ];
  return allSports.includes(n as SportOption) ? (n as SportOption) : null;
}

/** Map API `game_type` values to filter GameTypeOption ids. */
export function gameGameTypeFilterOption(game: LobbyGame): GameTypeOption | null {
  const gt = (game.game_type || '').toLowerCase();
  if (gt === 'squares') return 'squares';
  if (gt === 'daily_reveal' || gt === 'daily_draw') return 'daily_draw';
  if (gt === 'pickem') return 'pickems';
  if (gt === 'bingo') return 'bingo';
  // NOTE: 'survivor' and 'bracket' are still unmapped here. The filter chips for them already
  // exist, so they render and match nothing — the same gap Bingo had (spec §5.4). Left alone
  // rather than fixed in passing, since neither is this ticket.
  return null;
}

const GAME_TYPE_OPTION_ORDER: GameTypeOption[] = [
  'squares',
  'daily_draw',
  'bingo',
  'pickems',
  'survivor',
  'bracket',
];

const SPORT_OPTION_ORDER: SportOption[] = [
  'nba',
  'nfl',
  'mlb',
  'soccer',
  'tennis',
  'golf',
  'ice_hockey',
  'table_tennis',
  'track_and_field',
  'mls',
  'ufc',
  'nascar',
  'the_masters',
  'premier_league',
];

/** Unique game-type and sport filter ids present in the given lobby games. */
export function deriveLobbyFilterOptions(games: LobbyGame[]): {
  gameTypes: GameTypeOption[];
  sports: SportOption[];
} {
  const gameTypeSet = new Set<GameTypeOption>();
  const sportSet = new Set<SportOption>();

  for (const game of games) {
    const gameType = gameGameTypeFilterOption(game);
    if (gameType) gameTypeSet.add(gameType);
    const sport = gameSportFilterOption(game);
    if (sport) sportSet.add(sport);
  }

  return {
    gameTypes: GAME_TYPE_OPTION_ORDER.filter(id => gameTypeSet.has(id)),
    sports: SPORT_OPTION_ORDER.filter(id => sportSet.has(id)),
  };
}

function gameMatchesSportFilters(game: LobbyGame, selected: SportOption[]): boolean {
  if (selected.length === 0) return true;
  const id = gameSportFilterOption(game);
  if (!id) return false;
  return selected.includes(id);
}

function gameMatchesGameTypeFilters(game: LobbyGame, selected: GameTypeOption[]): boolean {
  if (selected.length === 0) return true;
  const id = gameGameTypeFilterOption(game);
  if (!id) return false;
  return selected.includes(id);
}

/**
 * Modal filters only (sport + game type). Timeframe is not applied — contests do not expose
 * schedule fields on this DTO yet.
 */
export function matchesLobbyModalFilters(game: LobbyGame, filters: LobbyFilters): boolean {
  if (!gameMatchesSportFilters(game, filters.sports)) return false;
  if (!gameMatchesGameTypeFilters(game, filters.gameTypes)) return false;
  return true;
}

export function sortLobbyGamesByFilters(games: LobbyGame[], filters: LobbyFilters): LobbyGame[] {
  const list = [...games];
  const title = (g: LobbyGame) => (g.title_en || g.title_fr || '').toLowerCase();
  const prize = (g: LobbyGame) => g.prize_text_en || '';

  list.sort((a, b) => {
    if (filters.prizePools === 'ascending') {
      const c = prize(a).localeCompare(prize(b));
      if (c !== 0) return c;
    } else if (filters.prizePools === 'descending') {
      const c = prize(b).localeCompare(prize(a));
      if (c !== 0) return c;
    }

    switch (filters.sortBy) {
      case 'az':
        return title(a).localeCompare(title(b));
      case 'za':
        return title(b).localeCompare(title(a));
      case 'popular':
        return a.display_order - b.display_order;
      case 'newest':
        return b.display_order - a.display_order;
      default:
        return a.display_order - b.display_order;
    }
  });

  return list;
}

/** Category chips + modal filters + sort (for lobby list + search “Suggested”). */
export function applyLobbyPipeline(
  games: LobbyGame[],
  filters: LobbyFilters,
  categoryPass: (game: LobbyGame) => boolean
): LobbyGame[] {
  const filtered = games.filter(g => categoryPass(g) && matchesLobbyModalFilters(g, filters));
  return sortLobbyGamesByFilters(filtered, filters);
}
